import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Alert, CircularProgress, Chip,
  LinearProgress, Grid,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import studentsService from '../../services/studentService';
import schoolService from '../../services/schoolService';
import { Student, School } from '../../types';
import { fetchSeatUsage, assignSeat, releaseSeat, SeatUsage } from '../../services/seatService';

/**
 * Seat manager for a school licence (#05).
 *
 * The licence is the source of truth for how many seats exist, so every figure on
 * this page is read back from the server after a change rather than adjusted
 * locally. Optimistically decrementing a seat count would let the screen disagree
 * with what the school is billed for, which is the one number here that must not
 * be a guess.
 */
const SeatsPage: React.FC = () => {
  const { schoolId } = useParams<{ schoolId: string }>();
  const navigate = useNavigate();

  const [school, setSchool] = useState<School | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [usage, setUsage] = useState<SeatUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyStudent, setBusyStudent] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!schoolId) return;
    setError(null);
    const [seatResult, studentResponse, schoolResponse] = await Promise.all([
      fetchSeatUsage(schoolId),
      studentsService.getStudentsBySchool(schoolId).catch(() => null),
      schoolService.getSchoolById(schoolId).catch(() => null),
    ]);

    if (seatResult.state === 'ready') setUsage(seatResult.usage);
    else setError(seatResult.message);

    if (studentResponse?.data) setStudents(studentResponse.data);
    if (schoolResponse?.data) setSchool(schoolResponse.data as School);
    setLoading(false);
  }, [schoolId]);

  useEffect(() => { void load(); }, [load]);

  const act = async (student: Student, assigned: boolean) => {
    if (!schoolId) return;
    setBusyStudent(student.id);
    setError(null);
    const failure = assigned
      ? await releaseSeat(schoolId, student.id)
      : await assignSeat(schoolId, student.id);
    if (failure) setError(failure);
    await load();               // re-read rather than adjust locally
    setBusyStudent(null);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  const assignedIds = new Set(usage?.assignedStudentIds ?? []);

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/schools')} sx={{ mb: 2 }}>
        Back to schools
      </Button>

      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Seats{school?.name ? ` — ${school.name}` : ''}
      </Typography>

      {error && <Alert severity="warning" sx={{ my: 2 }}>{error}</Alert>}

      {!usage?.hasLicence ? (
        <Alert severity="info" sx={{ my: 3 }}>
          This school has no seat licence. Seats become assignable once a licence is granted.
        </Alert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ my: 1 }}>
            {[
              { label: 'Plan', value: usage.plan ?? '—' },
              { label: 'Seats used', value: `${usage.seatsUsed} of ${usage.seatsTotal}` },
              { label: 'Available', value: usage.seatsAvailable },
              { label: 'Expires', value: usage.validTo ? new Date(usage.validTo).toLocaleDateString() : 'Never' },
            ].map((stat) => (
              <Grid size={{ xs: 6, md: 3 }} key={stat.label}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{stat.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {usage.utilisationPct != null && (
            <Box sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Utilisation {usage.utilisationPct}%
              </Typography>
              <LinearProgress variant="determinate" value={Math.min(100, usage.utilisationPct)} />
            </Box>
          )}
        </>
      )}

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Grade</TableCell>
              <TableCell>Seat</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {students.length === 0 && (
              <TableRow><TableCell colSpan={4}>No students enrolled at this school.</TableCell></TableRow>
            )}
            {students.map((student) => {
              const assigned = assignedIds.has(student.id);
              // Nothing left to give, and this student has not got one.
              const poolEmpty = !assigned && (usage?.seatsAvailable ?? 0) <= 0;
              return (
                <TableRow key={student.id}>
                  <TableCell>{student.fullName}</TableCell>
                  <TableCell>{student.grade ?? '—'}</TableCell>
                  <TableCell>
                    {assigned
                      ? <Chip size="small" color="success" label="Assigned" />
                      : <Chip size="small" variant="outlined" label="—" />}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={assigned ? 'text' : 'outlined'}
                      disabled={busyStudent === student.id || !usage?.hasLicence || poolEmpty}
                      title={poolEmpty ? 'No seats left in the licence' : ''}
                      onClick={() => act(student, assigned)}
                    >
                      {assigned ? 'Release' : 'Assign'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SeatsPage;
